import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Lifted verbatim out of `ManualWorkoutEditor` in Phase 7A so the container and
// both renderer branches share ONE sortable binding. dnd-kit stays; the
// container still owns the sensors and their activation constraints
// (PointerSensor 8px, TouchSensor 180ms/8px) and still derives sortable ids
// from `getBlockUiKey`, which preserves React identity across canonical
// replacement.
export function getBlockUiKey(workoutId, block, blockIndex) {
  const normalizedUiKey = String(block?.uiKey || "").trim();

  if (normalizedUiKey) {
    return normalizedUiKey;
  }

  return `${String(workoutId || "").trim() || "workout"}:index:${blockIndex}`;
}

// Only these block types have ever been sortable. Unsupported/advanced blocks
// stay out of the sortable id list.
export const SORTABLE_BLOCK_TYPES = ["single", "superset", "cardio"];

export function isSortableBlockType(type) {
  return SORTABLE_BLOCK_TYPES.includes(type);
}

export default function SortableBlock({ sortableId, disabled = false, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: sortableId,
    disabled,
  });

  // TRANSLATE ONLY, never `CSS.Transform.toString`. Block cards are genuinely
  // different heights (a Single is ~100px, an expanded Superset several hundred),
  // and dnd-kit's `useDerivedTransform` measures an item before and after its
  // index changes mid-sort, emitting `scaleX: initial.width / current.width` and
  // `scaleY: initial.height / current.height` alongside the translation. Rendering
  // that scale made the dragged card stretch toward a taller neighbour and shrink
  // toward a shorter one. `CSS.Translate.toString` keeps the movement and drops the
  // scale, so a dragged block keeps its own geometry for the whole gesture. The
  // sortable ids, the sensors and the reorder semantics are untouched.
  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  return children({
    setNodeRef,
    style,
    isDragging,
    handleAttributes: attributes,
    handleListeners: listeners,
    setHandleRef: setActivatorNodeRef,
  });
}
